<template>
  <div>
    <div class="d-flex flex-wrap align-center mb-3" style="gap: 12px">
      <v-autocomplete v-model="azra" :items="rasterItems" outlined dense hide-details label="Arbeitszeitraster (Stammdaten)"
                      style="max-width: 460px; min-width: 280px" no-data-text="Keine Arbeitszeitraster mit Phasen vorhanden."/>
      <v-btn-toggle v-model="ansicht" mandatory dense color="primary">
        <v-btn small value="raster"><v-icon small left>mdi-view-week-outline</v-icon>Raster</v-btn>
        <v-btn small value="kalender"><v-icon small left>mdi-calendar-clock</v-icon>Wochenkalender</v-btn>
      </v-btn-toggle>
    </div>

    <v-card v-if="data" outlined>
      <div class="d-flex align-center px-3 py-2 wb-head">
        <div>
          <div class="text-subtitle-2">{{ data.name }}</div>
          <div class="text-caption grey--text">Nr. {{ data.bis_id }} · {{ phasen.length }} {{ phasen.length === 1 ? 'Phase' : 'Phasen' }}</div>
        </div>
        <v-spacer/>
        <v-chip x-small outlined>Nur Lesen</v-chip>
      </div>
      <div class="d-flex align-center flex-wrap wb-bar">
        <v-tabs v-model="phaseIdx" show-arrows height="40" style="flex: 1 1 auto; max-width: 70%">
          <v-tab v-for="ph in phasen" :key="ph.von + ph.bis">{{ phaseLabel(ph) }}</v-tab>
        </v-tabs>
        <div class="d-flex px-2" style="gap: 8px">
          <v-text-field :value="wochenWertigkeit" label="Wochenstunden" outlined dense readonly hide-details style="width: 130px"/>
          <v-text-field :value="wochenSoll" label="Soll-Wochenstunden" outlined dense readonly hide-details style="width: 140px"/>
        </div>
      </div>
      <div class="d-flex align-center wb-bar">
        <v-tabs v-model="weekIdx" show-arrows height="38" style="flex: 1 1 auto">
          <v-tab v-for="w in phase.wochen" :key="w.nr">
            Woche #{{ w.nr }}<v-icon v-if="istGewaehlt(w)" small right color="primary">mdi-check</v-icon>
          </v-tab>
        </v-tabs>
        <div class="px-2 text-no-wrap">
          <v-btn icon small :disabled="weekIdx === 0" @click="weekIdx--" title="Vorherige Woche"><v-icon>mdi-chevron-left</v-icon></v-btn>
          <span class="text-caption">{{ weekIdx + 1 }} / {{ phase.wochen.length }}</span>
          <v-btn icon small :disabled="weekIdx >= phase.wochen.length - 1" @click="weekIdx++" title="Nächste Woche"><v-icon>mdi-chevron-right</v-icon></v-btn>
        </div>
      </div>
      <tages-raster v-if="woche" :tage="woche.tage" :ansicht="ansicht" class="pt-1"/>
      <div class="wb-foot text-caption">
        <v-icon small class="mr-1">{{ woche && woche.fehler ? 'mdi-alert' : 'mdi-information-outline' }}</v-icon>
        <span v-if="woche && woche.fehler" class="red--text">Diese Woche enthält einen unvollständigen oder rechnerisch falschen Tag und kann nicht zugeordnet werden.</span>
        <span v-else>Zeiten und Pausen kommen unverändert aus dem Arbeitszeitraster. Die Auswahl merkt sich Raster, Phase und Woche
          samt Tageswerten; das Jahresraster eines Mitarbeiters wird nicht verändert.</span>
      </div>
    </v-card>
    <div v-else-if="loading" class="pa-6 text-center"><v-progress-circular indeterminate color="primary"/></div>

    <div class="wb-selected d-flex align-center flex-wrap mt-3">
      <div>
        <div class="font-weight-medium">{{ value ? 'Ausgewählt: ' + value.raster_name + ' / Woche #' + value.woche_nr : 'Noch keine Musterwoche ausgewählt' }}</div>
        <div class="text-caption grey--text text--darken-1">
          <template v-if="value">Phase {{ phaseLabel({von: value.phase_von, bis: value.phase_bis}) }} · {{ hhmm(sum(value.tage)) }} Soll pro Woche</template>
          <template v-else>Woche über die Reiter oder Pfeile wählen und dann übernehmen.</template>
        </div>
      </div>
      <v-spacer/>
      <v-btn color="primary" depressed :disabled="!woche || !!woche.fehler" @click="auswaehlen">
        <v-icon left>{{ woche && istGewaehlt(woche) ? 'mdi-check' : 'mdi-plus' }}</v-icon>
        {{ woche && istGewaehlt(woche) ? 'Woche #' + woche.nr + ' ausgewählt' : 'Woche #' + (woche ? woche.nr : '') + ' auswählen' }}
      </v-btn>
    </div>
  </div>
</template>

<script>
import {fmtHHMM, fmtHours, phaseLabel, weekHours} from '@/shared/dienstplan_api'
import {dp, rasterWochen} from '../store'
import TagesRaster from './TagesRaster'

// Step 2 of the assignment: browse an existing pattern (phases and weeks,
// read-only) and select one pattern week.
export default {
  name: 'WochenBrowser',
  components: {TagesRaster},
  props: {
    value: {type: Object, default: null}, // selected pattern week
    preset: {type: Object, default: null}, // {azra, phase_von, woche_nr} to open
  },
  data: () => ({azra: null, phaseIdx: 0, weekIdx: 0, ansicht: 'raster', loading: false, presetWeek: null}),
  computed: {
    rasterItems () {
      return dp.basis.raster.map(r => ({value: r.bis_id, text: r.name + ' (' + r.bis_id + ')'}))
    },
    data () {
      return this.azra ? dp.wochen[this.azra] : null
    },
    phasen () {
      return this.data ? this.data.phasen : []
    },
    phase () {
      return this.phasen[this.phaseIdx] || {wochen: []}
    },
    woche () {
      return this.phase.wochen[this.weekIdx] || null
    },
    wochenWertigkeit () {
      return this.woche ? fmtHours(this.woche.tage.reduce((s, d) => s + (d.valence_hours || 0), 0)) : ''
    },
    wochenSoll () {
      return this.woche ? fmtHHMM(weekHours(this.woche.tage)) : ''
    },
  },
  watch: {
    azra (id) {
      if (!id) return
      this.loading = true
      rasterWochen(id).then(() => {
        this.loading = false
        this.applyPreset()
      }, () => { this.loading = false })
    },
    phaseIdx () {
      if (!this.presetPending && this.presetWeek == null) this.weekIdx = 0
    },
    weekIdx (v) {
      if (this.presetWeek != null && v !== this.presetWeek) this.weekIdx = this.presetWeek
    },
    preset: {
      immediate: true,
      handler (p) {
        if (!p || !p.azra) {
          if (!this.azra && dp.basis.raster.length) this.azra = dp.basis.raster[0].bis_id
          return
        }
        this.presetPending = true
        if (this.azra === p.azra && this.data) this.applyPreset()
        else this.azra = p.azra
      },
    },
  },
  methods: {
    phaseLabel,
    hhmm: fmtHHMM,
    sum: weekHours,
    applyPreset () {
      const p = this.preset
      if (!this.presetPending || !p || p.azra !== this.azra) return
      const pi = this.phasen.findIndex(ph => ph.von === p.phase_von)
      this.phaseIdx = pi >= 0 ? pi : 0
      this.$nextTick(() => {
        // v-tabs resets its value while the new week tabs render; hold the
        // preset week until they are in place
        this.presetWeek = p.woche_nr ? Math.max(0, Math.min(p.woche_nr - 1, this.phase.wochen.length - 1)) : 0
        this.weekIdx = this.presetWeek
        this.presetPending = false
        setTimeout(() => { this.presetWeek = null }, 400)
      })
    },
    istGewaehlt (w) {
      const v = this.value
      return !!v && v.azra === this.azra && v.phase_von === this.phase.von && v.woche_nr === w.nr
    },
    auswaehlen () {
      const w = this.woche
      this.$emit('input', {azra: this.azra, raster_name: this.data.name, phase_von: this.phase.von, phase_bis: this.phase.bis,
        woche_nr: w.nr, hash: w.hash, tage: w.tage})
    },
  },
}
</script>

<style scoped>
.wb-head {
  border-bottom: 1px solid #e0e0e0;
}
.wb-bar {
  border-bottom: 1px solid #e0e0e0;
}
.wb-foot {
  background: #fafafa;
  border-top: 1px solid #e0e0e0;
  padding: 8px 12px;
  color: #616161;
}
.wb-selected {
  background: #f3f4fc;
  border-left: 3px solid #3f51b5;
  padding: 10px 14px;
  gap: 10px;
}
</style>
